<template>
  <div
    class="text-left p-2 text-gray-700 font-semibold text-xl tracking-wider uppercase"
  >
    Files
  </div>
  <template v-if="loading">Loading...</template>
  <template v-else-if="files.length > 0">
    <Table
      :headers="
        ['Nature', 'Original Name', 'Actions', 'Updated At', 'Size'].concat(
          hasFileWriteRight ? ['Archive File'] : []
        )
      "
    >
      <tr
        class="border-b border-gray-200"
        v-for="file of orderedFileList"
        :key="file.node.id"
        :class="{
          'bg-gray-100': isCurrentFileByNature(file, orderedFileList),
          'cursor-pointer': hasFileReadRight,
          'cursor-not-allowed': !hasFileReadRight,
        }"
        @click="hasFileReadRight ? showFileInfo(file.node.id) : null"
      >
        <td class="p-3">
          {{ file.node.nature }}
        </td>
        <td>
          <div class="p-3 text-gray-500 download-file">
            {{ file.node.originalName || 'Unknown' }}
            <download-file-button
              :file="file.node"
              buttonSize="small"
            ></download-file-button>
          </div>
        </td>

        <td>
          <tr class="flex flex-row flex-wrap">
            <file-trigger-action
              :file="file.node"
              :entity-id="props?.entityId"
              :extracted-from-file-id="props?.extractedFromFileId"
              :disableAction="!canBeRepacked(file)"
              :fileList="files"
              buttonSize="xs"
            ></file-trigger-action>
          </tr>
        </td>
        <td class="p-3">
          <dm-date :date="file.node.updated" />
        </td>
        <td class="p-3">
          {{ readableFileSize(file.node.size) }}
        </td>
        <td v-if="hasFileWriteRight" class="text-center">
          <file-archival-button
            :file="file.node"
            @archived="updateFiles"
            button-size="small"
          />
        </td>
      </tr>
    </Table>
    <template v-if="showMoreFilesBtn">
      <div class="flex mt-1">
        <dm-button
          variant="simple"
          size="xs"
          @click="showMoreFiles = !showMoreFiles"
        >
          {{ showMoreFiles ? 'Show Less' : 'Show More' }} Files
        </dm-button>
      </div>
    </template>
  </template>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { computed } from '@vue/reactivity';

import { graphql } from '../../__generated__/gql';
import {
  FulfillmentMethod,
  LcpRepresentation,
  AcsRepresentation,
  CantookAudioRepresentation,
} from '../../__generated__/graphql';

import { router } from '../../routes';
import FileTriggerAction from './FileTriggerAction.vue';
import FileArchivalButton from './FileArchivalButton.vue';
import DownloadFileButton from './DownloadFileButton.vue';
import { readableFileSize } from '../../utils';
import Table from '../base/Table.vue';
import { useAuth } from '../../auth';
import { Ref, ref } from 'vue';

const props = defineProps<{
  entityId: string;
  extractedFromFileId: string | null;
}>();

const emits = defineEmits<{
  (e: 'no-files'): void;
}>();

const {
  canWriteResourceTypeOnAtLeastOneNamespace,
  canReadResourceTypeOnAtLeastOneNamespace,
} = useAuth();

const hasFileWriteRight = canWriteResourceTypeOnAtLeastOneNamespace('files');
const hasFileReadRight = canReadResourceTypeOnAtLeastOneNamespace('files');
const showMoreFiles = ref(false);
const showMoreFilesBtn = ref(false);

type Files = typeof files.value;
const files = computed(() => {
  const maybeFiles = result?.value?.publication?.files?.edges;
  if (maybeFiles) {
    if (maybeFiles.length === 0) {
      emits('no-files');
    }
    return maybeFiles;
  } else {
    return [];
  }
});

const sortFilesByDate = (filesToSort: Files): Files => {
  return filesToSort.sort((fileA, fileB) => {
    return (
      new Date(fileB.node.updated).getTime() -
      new Date(fileA.node.updated).getTime()
    );
  });
};
/*
For each nature, the current file is either
the last file that has been manually uploaded
or the last auto-generated file depending on its updated date.
*/
const isCurrentFileByNature = (file: Files[number], orderedFileList: Files) => {
  const filesWithSameNature = orderedFileList.filter(
    (filterFile) => filterFile.node.nature === file.node.nature
  );

  const filesNotDerivedFromAnother = filesWithSameNature.filter(
    (filterFile) => filterFile.node.derivedFrom === null
  );

  const currentFile = filesNotDerivedFromAnother[0]
    ? filesNotDerivedFromAnother[0]
    : filesWithSameNature[0];

  return file === currentFile;
};

const mostRecentContentFileWithRepresentations: Ref<null | Files[number]> =
  ref(null);
const mostRecentCoverFile: Ref<null | Files[number]> = ref(null);
const mostRecentSampleFile: Ref<null | Files[number]> = ref(null);

/*
A file can be repacked if it is the most recent file of its nature
or if its package isn't applied
*/
const canBeRepacked = (file: Files[number]) => {
  switch (file.node.nature) {
    case 'SAMPLE':
      return (
        new Date(file.node.updated) >=
        new Date(mostRecentSampleFile.value?.node.updated)
      );
    case 'COVER':
      return (
        new Date(file.node.updated) >=
        new Date(mostRecentCoverFile.value?.node.updated)
      );
    case 'CONTENT':
      if (
        mostRecentContentFileWithRepresentations.value?.node.updated == null
      ) {
        return true;
      }
      return (
        new Date(file.node.updated) >=
        new Date(mostRecentContentFileWithRepresentations.value?.node.updated)
      );
    default:
      return true;
  }
};

const orderFileList = (fileList: Files) => {
  if (fileList.length === 0) {
    return [];
  }

  const sortedFiles = sortFilesByDate(fileList);

  mostRecentContentFileWithRepresentations.value =
    sortedFiles.find(
      (file) =>
        file.node.nature === 'CONTENT' &&
        file.node.representations &&
        file.node.representations.length > 0
    ) || null;

  mostRecentCoverFile.value =
    sortedFiles.find((file) => file.node.nature === 'COVER') || null;
  mostRecentSampleFile.value =
    sortedFiles.find(
      (file) =>
        (file.node.nature === 'SAMPLE' && file.node.derivedFrom == null) ||
        file.node.nature === 'SAMPLE'
    ) || null;

  const tempCurrentFileList = sortedFiles.filter((file) =>
    isCurrentFileByNature(file, sortedFiles)
  );

  const tempMetadataFileList = sortedFiles.filter(
    (file) =>
      file.node.nature === 'PUBLICATION_METADATA' &&
      !tempCurrentFileList.includes(file)
  );

  const filteredFiles = sortedFiles.filter(
    (file) =>
      !tempMetadataFileList.includes(file) &&
      !tempCurrentFileList.includes(file)
  );

  const orderedFileList = [
    ...tempCurrentFileList,
    ...filteredFiles,
    ...tempMetadataFileList,
  ];

  if (orderedFileList.length !== tempCurrentFileList.length) {
    showMoreFilesBtn.value = true;
  }

  return showMoreFiles.value ? orderedFileList : tempCurrentFileList;
};

const orderedFileList = computed(() => {
  return orderFileList([...files.value]);
});

const showFileInfo = (fileId: string) => {
  router.push({ name: 'file', params: { id: fileId } });
};

const {
  result,
  loading,
  refetch: updateFiles,
} = useQuery(
  graphql(`
    query GetFiles($publicationId: ID!) {
      publication(id: $publicationId) {
        files {
          edges {
            node {
              id
              mime
              md5Hash
              originalName
              location
              nature
              updated
              size
              derivedFrom {
                id
              }
              representations {
                __typename
              }
            }
          }
        }
      }
    }
  `),
  () => ({ publicationId: props.entityId })
);

const fulfillmentMethodsFiles = (key: FulfillmentMethod) =>
  computed(() => {
    const contentFiles = files.value.filter(
      (file) => file.node.nature === 'CONTENT'
    );

    if (!contentFiles || contentFiles.length === 0) {
      return;
    }

    const sortedContentFiles = sortFilesByDate(contentFiles);

    if (key === 'WEBREADER' || key === 'DIRECT_DOWNLOAD') {
      return {
        availability: `The latest content file will be served to the readers: ${sortedContentFiles[0].node.id}`,
        id: contentFiles[0].node.id,
      };
    }

    const availability =
      'The last uploaded file with applied protection will be served to the readers';

    if (key === 'LCP') {
      const currentLcpContentFile = sortedContentFiles.find((file) =>
        fileHasRepresentation(
          file.node.representations as (
            | LcpRepresentation
            | AcsRepresentation
            | CantookAudioRepresentation
          )[],
          'LcpRepresentation'
        )
      );

      if (currentLcpContentFile) {
        return {
          availability: `${availability}: ${currentLcpContentFile.node.id}`,
          id: currentLcpContentFile.node.id,
        };
      }
    }

    if (key === 'ACS4') {
      const currentAcsContentFile = sortedContentFiles.find((file) =>
        fileHasRepresentation(
          file.node.representations as (
            | LcpRepresentation
            | AcsRepresentation
            | CantookAudioRepresentation
          )[],
          'AcsRepresentation'
        )
      );

      if (currentAcsContentFile) {
        return {
          availability: `${availability}: ${currentAcsContentFile.node.id}`,
          id: currentAcsContentFile.node.id,
        };
      }
    }

    if (key === 'AUDIO_MANIFEST') {
      const currentAudioContentFile = sortedContentFiles.find((file) =>
        fileHasRepresentation(
          file.node.representations as (
            | LcpRepresentation
            | AcsRepresentation
            | CantookAudioRepresentation
          )[],
          'CantookAudioRepresentation'
        )
      );

      if (currentAudioContentFile) {
        return {
          availability: `${availability}: ${currentAudioContentFile.node.id}`,
          id: currentAudioContentFile.node.id,
        };
      }
    }
  });

const fileHasRepresentation = (
  fileRepresentations: (
    | AcsRepresentation
    | LcpRepresentation
    | CantookAudioRepresentation
  )[],
  representationTypename: string
) => {
  return fileRepresentations.some(
    (fileRepresentation) =>
      fileRepresentation.__typename === representationTypename
  );
};

defineExpose({ updateFiles, fulfillmentMethodsFiles });
</script>

<style>
.download-file {
  display: inline-flex;
  text-align: center;
  vertical-align: center;
}

.files-display {
  display: grid;
}
</style>
